import { analystSectionGapsOf } from '../protocol/analyst-section.js';
import type { ProtocolOrphanStep } from '../protocol/problems.js';
import type { AnalystLevel } from '../schemas/analyst.js';
import {
  type DisplayCondition,
  EXPERIENCE_SELECTION_OPTIONS_MAX,
  EXPERIENCE_SELECTION_OPTIONS_MIN,
  type ExperienceContent,
  type ExperienceProblem,
  type ExperienceProductionBlocker,
  type ExperienceReferenceKind,
  type ExperienceWarning,
} from '../schemas/experience.js';
import { NOYAU_TEXTS_ID } from '../schemas/noyau-texts.js';
import { type VersionedRef, pinKey } from '../schemas/versioning.js';
import { displayConditionVariable } from './conditions.js';
import { conversationRunOf, pageHasConversationExit } from './conversation.js';
import { type NoyauBlockSite, firstChoiceOf, noyauBlocksOf, noyauTextsPinOf } from './noyau.js';
import { isFieldBlock, isPersonNameInput, pageBlocks, pageChoiceName } from './play.js';
import {
  type ExperienceVariable,
  type VariablePlace,
  experienceTextSitesOf,
  experienceVariableToken,
  experienceVariablesOf,
  namedVariablesOf,
  protocolPageOf,
  variableScopeBreach,
} from './variables.js';

/**
 * What « Enregistrer une nouvelle version » and « Passer en production » refuse
 * on an experience (§9.1, §8.6), and the one thing they only WARN about.
 *
 * PURE, and in `shared` for the reason `protocol/problems.ts` gives: the editor
 * runs the very same rules to grey its button and to fill the red encart of the
 * save box, and the server runs them to refuse. Two spellings of one rule show
 * up as a click that goes through to a 409 nobody expected.
 *
 * ## What the rules cannot know by themselves
 *
 * Three of the four refusals of §9.1 are answered by the content alone: a
 * protocol chosen without a persona, two variables of the same name, a
 * `{{champ:...}}` or `{{choix:...}}` pointing at a block or a page the timeline
 * no longer holds - and so is the fifth, the REACH of §8.6 (grill Variables of
 * 2026-09-16, D3), which is a question about the ORDER of the timeline. The
 * fourth - a file of the library that is gone - and the two variables that
 * reach OUTSIDE the experience - `{{objectif:...}}`, `{{critere:...}}` - are
 * questions only the base can answer, so they arrive as a LOOKUP, the
 * arrangement `productionBlockers` already uses for a protocol's criteria. The
 * caller decides how often the base is read; this file stays pure.
 *
 * A caller that has no lookup to give - the editor, before it has read the
 * library - passes one that answers `true`, and gets the three refusals it can
 * compute. The server always has the real one, and it is the server that
 * refuses.
 */

/** What the base knows and a content does not (§9.1). */
export type ExperienceContext = {
  /** Does the media library still hold this file? A hard delete: the file is gone. */
  readonly hasAsset: (assetId: string) => boolean;
  /** Does the protocol this timeline plays still carry this objective (§8.6)? */
  readonly hasObjective: (objectiveId: string) => boolean;
  /** Does the base still hold this criterion, un-retired (§8.6)? */
  readonly hasCriterion: (criterionId: string) => boolean;
};

/**
 * The version one pin of a protocol resolves to, as the rule of noyau.md §3.7 and
 * analyste.md §3.6 reads its Analyste section (issues #154, #196): its number,
 * the level of every objective that carries one, and the two readings as they are
 * stored - `analystSectionGapsOf` decides, spaces alone reading as none (N35).
 *
 * Raw facts and not a verdict, so a lookup built from the base and a lookup built
 * in a test give the pure rule the same thing to judge.
 */
export type ProtocolAnalystAtVersion = {
  readonly versionNumber: number;
  /**
   * The level of each objective of the version that has one, in the order of the
   * protocol - or `null` when the STEPS of the version do not read (a hand edit,
   * a defect): its levels are on them, so it has none to count, and no
   * `analyst-levels-missing` is claimed. The two readings below are stored apart
   * from the steps and are judged all the same.
   */
  readonly levels: readonly AnalystLevel[] | null;
  /** The reading of the first pass (« Lecture de la passe 1 »). */
  readonly functionsReading: string;
  /** The reading of the second pass (« Lecture de la passe 2 »). */
  readonly styleReading: string;
};

/**
 * The version one pin of a protocol resolves to, as §8.6 of the refonte reads
 * its DIRECTIONS (issue #158, V5, V6, N53): its number, and each step whose
 * directions cite an objective that version no longer carries - what
 * `protocolOrphans` finds on its content, empty when there is none.
 */
export type ProtocolOrphansAtVersion = {
  readonly versionNumber: number;
  readonly steps: readonly ProtocolOrphanStep[];
};

/**
 * What a lookup keys the facts of a protocol on: `latest`, or the version number
 * as text (`pinKey`). `latest` is read on the DEVELOPMENT pointer - « latest
 * dev » - because every gesture of this file is made on the atelier, and the
 * cascade of « Passer en production » carries the version the dev plays at that
 * instant (vitrine.md §4.2).
 */
export type PinKeyed<Fact> = Readonly<Record<string, Fact>>;

/** The lookup of a referenced object's state - the second half of the production check. */
export type ExperienceReferenceState = {
  readonly name: string;
  readonly hasProduction: boolean;
  readonly retiredAt: string | null;
  /**
   * PROTOCOLS only, since issue #154 (noyau.md §3.7, N36): the version each pin
   * of interest resolves to and its Analyste section (the levels its objectives
   * carry and its two readings since #196, analyste.md §3.6), keyed by `pinKey` -
   * a pin with no version left out.
   *
   * Optional, because only a caller that READ the versions can give it: the
   * server always does, and it is the server that refuses and warns. A lookup
   * built from the library's cards - the editor's, for the pointer box of the
   * history - leaves it out and claims no `analyst-levels-missing` nor
   * `analyst-reading-missing`; the gesture then meets the server's refusal,
   * worded like every 409 of the layer.
   */
  readonly analystSections?: PinKeyed<ProtocolAnalystAtVersion>;
  /**
   * PROTOCOLS only, since issue #158 (§8.6 of the refonte, N53): the version
   * each pin resolves to and the steps whose directions cite an objective it no
   * longer carries, keyed by `pinKey` - a pin with no version left out.
   *
   * Optional, under the contract of `analystSections` and for its reason: a
   * lookup that did not read the versions claims no `protocol-variable-orphan`.
   */
  readonly orphanDirections?: PinKeyed<ProtocolOrphansAtVersion>;
};

export type ExperienceReferenceLookup = (
  kind: ExperienceReferenceKind,
  id: string,
) => ExperienceReferenceState | undefined;

/* --------------------------------------------------------- the two citations */

// `protocolPageOf` lives in `variables.ts` since the grill Variables: the reach
// rule reads it too, and one reading of « the protocol page » is the point.

/**
 * The versioned objects an experience cites (§4): the protocol of its protocol
 * page, its persona - both optional, both by `{ id, pin }` - and, since issue
 * #151, the TEXTS OF THE NOYAUX once a Noyau block is posed (noyau.md §3.5).
 *
 * The texts are cited by a PIN alone (`noyauTexts: { pin }`, N13), so their
 * reference is spelled here under the singleton's constant, with the pin
 * `noyauTextsPinOf` reads - the one reading of the reference, for the cascade
 * of a start as for this check: a content without a Noyau block cites nothing
 * whatever its `noyauTexts` says, and a block without a reference cites
 * `latest`, the default of every reference (V16).
 *
 * This is the reference extractor of §4 in its pure form; the module in
 * `packages/backend/src/experience/store.ts` is the one that turns it into the
 * `{ kind, id, pin }` the usage walk takes - and « Passer en production », the
 * warnings of the save box and the production pointer of the history all walk
 * this very list, so the texts are checked there like the two others.
 */
export function experienceReferences(
  content: ExperienceContent,
): { kind: ExperienceReferenceKind; ref: VersionedRef }[] {
  const references: { kind: ExperienceReferenceKind; ref: VersionedRef }[] = [];
  const page = protocolPageOf(content);
  if (page?.protocol != null) references.push({ kind: 'protocol', ref: page.protocol });
  if (content.persona !== null) references.push({ kind: 'persona', ref: content.persona.ref });
  const textsPin = noyauTextsPinOf(content);
  if (textsPin !== null) {
    references.push({ kind: 'noyau-texts', ref: { id: NOYAU_TEXTS_ID, pin: textsPin } });
  }
  return references;
}

/* ------------------------------------------------------------- the problems */

/** Every file citation WITH its place, so a missing file names where it is used. */
function assetCitations(content: ExperienceContent): {
  assetId: string;
  pageId: string | null;
  pageIndex: number | null;
  pageName: string | null;
  blockId: string | null;
}[] {
  const cited: {
    assetId: string;
    pageId: string | null;
    pageIndex: number | null;
    pageName: string | null;
    blockId: string | null;
  }[] = [];
  const nowhere = { pageId: null, pageIndex: null, pageName: null, blockId: null };

  const background = content.theme.backgroundImageAssetId;
  if (typeof background === 'string' && background !== '') {
    cited.push({ assetId: background, ...nowhere });
  }

  content.pages.forEach((page, pageIndex) => {
    const place = { pageId: page.id, pageIndex, pageName: page.name };
    const pageBackground = page.visual.backgroundImageAssetId;
    if (typeof pageBackground === 'string' && pageBackground !== '') {
      cited.push({ assetId: pageBackground, ...place, blockId: null });
    }
    for (const temps of page.temps) {
      for (const block of temps.blocks) {
        if (block.type !== 'image' && block.type !== 'sound') continue;
        if (block.assetId === null) continue;
        cited.push({ assetId: block.assetId, ...place, blockId: block.id });
      }
    }
  });

  return cited;
}

/**
 * Can this variable be resolved by the timeline itself (§8.6)?
 *
 * A `{{champ:<blockId>}}` needs a block that WRITES a value - an Input, a
 * Checkbox, a Sélection or a Note; an image block with that id would be a token
 * that renders nothing.
 * A `{{choix:<pageId>}}` needs a page whose choice the session WRITES, and that
 * is `pageChoiceName`'s predicate and nobody else's: « un bouton seul n'écrit
 * rien », so a page with one button, or with remembering buttons none of which
 * is named, never fills that variable - and a save that accepted the token
 * would let the person read a blank where a word was promised.
 */
function resolvedByTimeline(content: ExperienceContent, variable: ExperienceVariable): boolean {
  if (variable.kind === 'field') {
    return content.pages.some((page) =>
      page.temps.some((temps) =>
        temps.blocks.some((block) => block.id === variable.id && isFieldBlock(block)),
      ),
    );
  }
  const page = content.pages.find((candidate) => candidate.id === variable.id);
  return page !== undefined && pageChoiceName(page) !== null;
}

/**
 * Whether the block a DISPLAY CONDITION names exists, with the nature the
 * condition gives it (§9.2 of the refonte): a `checkbox` condition needs a
 * Checkbox of that id, an `input` one an Input, a `choice` one a page that
 * writes its choice - `resolvedByTimeline`'s own predicate - and, for every
 * Bouton the condition names, a Bouton of that page that CHANGES PAGE. A
 * condition that names a Checkbox by an Input's id resolves to nothing, which
 * is how a nature and its block stay in step (`displayConditionSchema`). A
 * Bouton deleted since, and a Bouton « temps suivant » - which never leaves the
 * page, so never writes its choice (§8.6) - are two values no press can give:
 * the condition would be false for everyone without a word.
 */
function conditionResolvedByTimeline(
  content: ExperienceContent,
  condition: Extract<DisplayCondition, { kind: 'choice' | 'checkbox' | 'input' }>,
): boolean {
  if (condition.kind === 'choice') {
    const page = content.pages.find((candidate) => candidate.id === condition.pageId);
    if (page === undefined || pageChoiceName(page) === null) return false;
    const exits = new Set(
      pageBlocks(page).flatMap((block) =>
        block.type === 'button' && block.action === 'next-page' ? [block.id] : [],
      ),
    );
    return condition.buttonIds.every((id) => exits.has(id));
  }
  return content.pages.some((page) =>
    pageBlocks(page).some(
      (block) => block.id === condition.blockId && block.type === condition.kind,
    ),
  );
}

/**
 * ONE variable cited somewhere in a content: by a token of a text (§8.6), or by
 * a display condition of a block (§9.2). The same rules judge both - « comme
 * une pastille » - and the problem says which it was (`inCondition`).
 */
type Citation = {
  readonly pageId: string | null;
  readonly pageIndex: number | null;
  readonly pageName: string | null;
  readonly blockId: string | null;
  readonly reach: VariablePlace;
  readonly variable: ExperienceVariable;
  readonly condition: DisplayCondition | null;
};

/** Every citation of a content, the text tokens first, then the conditions, each in reading order. */
function citationsOf(content: ExperienceContent): Citation[] {
  const citations: Citation[] = [];
  for (const site of experienceTextSitesOf(content)) {
    for (const variable of experienceVariablesOf(site.text)) {
      citations.push({
        pageId: site.pageId,
        pageIndex: site.pageIndex,
        pageName: site.pageName,
        blockId: site.blockId,
        reach: site.place,
        variable,
        condition: null,
      });
    }
  }
  content.pages.forEach((page, pageIndex) => {
    for (const block of pageBlocks(page)) {
      for (const condition of block.conditions) {
        citations.push({
          pageId: page.id,
          pageIndex,
          pageName: page.name,
          blockId: block.id,
          reach: { kind: 'page', pageIndex },
          variable: displayConditionVariable(condition),
          condition,
        });
      }
    }
  });
  return citations;
}

/** Whether a citation resolves at all - before its reach is judged. */
function citationResolves(
  content: ExperienceContent,
  context: ExperienceContext,
  citation: Citation,
): boolean {
  const { variable, condition } = citation;
  if (variable.kind === 'objective') return context.hasObjective(variable.id);
  if (variable.kind === 'criterion') return context.hasCriterion(variable.id);
  if (variable.kind === 'noyau') return firstChoiceOf(content) !== null;
  if (condition !== null && condition.kind !== 'noyau' && condition.kind !== 'objective') {
    return conditionResolvedByTimeline(content, condition);
  }
  return resolvedByTimeline(content, variable);
}

/**
 * The refusals of the VARIABLES (§8.6, D3; §9.2 for a condition; noyau.md §3.3
 * for `@noyau`), in the order of the citations.
 *
 * `@noyau` with no first choice anywhere is not « unresolved » but
 * `noyau-first-choice-missing` (noyau.md §3.4): what is missing is a block the
 * operator has to add, not a token to correct.
 */
function variableProblems(
  content: ExperienceContent,
  context: ExperienceContext,
): ExperienceProblem[] {
  const problems: ExperienceProblem[] = [];
  for (const citation of citationsOf(content)) {
    const { variable, condition } = citation;
    const where = {
      pageId: citation.pageId,
      pageIndex: citation.pageIndex,
      pageName: citation.pageName,
      blockId: citation.blockId,
    };
    if (!citationResolves(content, context, citation)) {
      if (variable.kind === 'noyau') {
        problems.push({
          kind: 'noyau-first-choice-missing',
          ...where,
          needs: condition === null ? 'variable' : 'condition',
        });
        continue;
      }
      problems.push({ kind: 'unresolved-variable', ...where, ...citedAs(citation) });
      continue;
    }
    // The token resolves, but does it resolve WHERE it is cited (§8.6, D3)?
    // « La personne verrait le jeton brut » - a value that does not exist yet
    // on the page that shows it is, for the person, the same as no value; and a
    // condition on it would be judged on nothing, for the same reason.
    const breach = variableScopeBreach(content, citation.reach, variable);
    if (breach === null) continue;
    problems.push({
      kind: 'variable-out-of-scope',
      ...where,
      ...citedAs(citation),
      reason: breach.reason,
      declaredPageIndex: breach.declaredPageIndex,
      declaredPageName: breach.declaredPageName,
    });
  }
  return problems;
}

/** The token, the kind and the id a variable refusal names - and whether a condition cited it. */
function citedAs(citation: Citation): {
  token: string;
  variableKind: ExperienceVariable['kind'];
  variableId: string;
  inCondition?: true;
} {
  return {
    token: experienceVariableToken(citation.variable),
    variableKind: citation.variable.kind,
    variableId: citation.variable.id,
    ...(citation.condition === null ? {} : { inCondition: true as const }),
  };
}

/** Where a noyau block sits, as a problem names it. */
function placeOfBlock(site: NoyauBlockSite): {
  pageId: string;
  pageIndex: number;
  pageName: string;
  blockId: string;
} {
  return {
    pageId: site.pageId,
    pageIndex: site.pageIndex,
    pageName: site.pageName,
    blockId: site.blockId,
  };
}

/**
 * The rules of the TIMELINE of the noyau (noyau.md §3.4, N9, N10): where the
 * Noyau blocks may sit, how many of each mode, the choice as the only way out
 * of its page, and a page after it for that way out to lead to.
 *
 * Judged on the blocks alone - the texts' `@noyau` are judged with the other
 * variables. The second choice is FACULTATIVE (N10): an experience may play a
 * first choice then a portrait, and nothing here asks for a second.
 */
function noyauTimelineProblems(content: ExperienceContent): ExperienceProblem[] {
  const problems: ExperienceProblem[] = [];
  const protocol = protocolPageOf(content);
  const blocks = noyauBlocksOf(content);
  const first = firstChoiceOf(content);

  // « un bloc Noyau sur une page qui ne vient pas APRÈS la page protocole » -
  // the protocol page itself included: nothing is recueilli before it closes.
  for (const site of blocks) {
    if (protocol === null || site.pageIndex > protocol.pageIndex) continue;
    problems.push({
      kind: 'noyau-before-protocol',
      ...placeOfBlock(site),
      protocolPage: { pageIndex: protocol.pageIndex, pageName: protocol.pageName },
    });
  }

  // One first choice, one second choice: the second and every later one of a
  // mode is refused, and the first of that mode is named - it is the one kept.
  for (const mode of ['first-choice', 'second-choice'] as const) {
    const [kept, ...repeated] = blocks.filter((site) => site.mode === mode);
    if (kept === undefined) continue;
    for (const site of repeated) {
      problems.push({
        kind: 'noyau-choice-repeated',
        ...placeOfBlock(site),
        mode,
        firstPage: { pageIndex: kept.pageIndex, pageName: kept.pageName },
      });
    }
  }

  // The second choice and the portrait read the RETAINED noyau: they need the
  // first choice, and they need to come after it - its own page included, since
  // the retained noyau exists from the page that FOLLOWS it (§3.3).
  for (const site of blocks) {
    if (site.mode === 'first-choice') continue;
    if (first === null) {
      problems.push({
        kind: 'noyau-first-choice-missing',
        ...placeOfBlock(site),
        needs: site.mode,
      });
      continue;
    }
    if (site.pageIndex > first.pageIndex) continue;
    problems.push({
      kind: 'noyau-out-of-order',
      ...placeOfBlock(site),
      mode: site.mode,
      firstChoicePage: { pageIndex: first.pageIndex, pageName: first.pageName },
    });
  }

  // « Ce bouton appartient au bloc : c'est lui, et lui seul, la sortie de la
  // page » (§3.2). A Bouton that plays the NEXT TEMPS stays allowed - an
  // introduction, then the cards - and so does a portrait, which is a block to
  // read and no way out.
  content.pages.forEach((page, pageIndex) => {
    const where = { pageId: page.id, pageIndex, pageName: page.name };
    const choices = pageBlocks(page).filter(
      (block) => block.type === 'noyau' && block.mode !== 'portrait',
    );
    if (choices.length === 0) return;
    for (const extra of choices.slice(1)) {
      problems.push({
        kind: 'noyau-choice-page-exit',
        ...where,
        blockId: extra.id,
        exit: 'choice',
      });
    }
    for (const block of pageBlocks(page)) {
      if (block.type !== 'button' || block.action !== 'next-page') continue;
      problems.push({
        kind: 'noyau-choice-page-exit',
        ...where,
        blockId: block.id,
        exit: 'next-page-button',
      });
    }
    if (page.autoDurationSeconds !== null) {
      problems.push({
        kind: 'noyau-choice-page-exit',
        ...where,
        blockId: null,
        exit: 'auto-duration',
      });
    }
    // « Choisir ce texte » confirms AND leads to the next page (§3.2): on the
    // last page there is none, the session ends on arrival - the reader then
    // deals two cards that no press can confirm, and the choice is never
    // recorded (review of !464). Named on the block that is the way out.
    const [choice] = choices;
    if (choice !== undefined && pageIndex === content.pages.length - 1) {
      problems.push({ kind: 'noyau-choice-last-page', ...where, blockId: choice.id });
    }
  });

  return problems;
}

/**
 * « La sortie d'une page ne se masque pas » (H69): a Bouton, whichever its
 * action, and a Noyau block in a choice mode carry NO display condition - a
 * page whose only way out were hidden would leave a person with no way out.
 * The rule is on the TYPE and not on what the page happens to hold besides.
 */
function exitConditionProblems(content: ExperienceContent): ExperienceProblem[] {
  const problems: ExperienceProblem[] = [];
  content.pages.forEach((page, pageIndex) => {
    for (const block of pageBlocks(page)) {
      if (block.conditions.length === 0) continue;
      const blockType =
        block.type === 'button'
          ? 'button'
          : block.type === 'noyau' && block.mode !== 'portrait'
            ? 'noyau'
            : null;
      if (blockType === null) continue;
      problems.push({
        kind: 'condition-on-exit',
        pageId: page.id,
        pageIndex,
        pageName: page.name,
        blockId: block.id,
        blockType,
      });
    }
  });
  return problems;
}

/**
 * « Nom de la personne dans Sessions », AT MOST ONE per experience (H75): the
 * second and every later Input carrying it is refused, with the page of the
 * first, which keeps it. Counted by `isPersonNameInput`, the reading Sessions
 * makes of it too - a multi-line Input carrying the flag names nobody, so it
 * holds no place here either.
 */
function personNameProblems(content: ExperienceContent): ExperienceProblem[] {
  const holders: { pageId: string; pageIndex: number; pageName: string; blockId: string }[] = [];
  content.pages.forEach((page, pageIndex) => {
    for (const block of pageBlocks(page)) {
      if (!isPersonNameInput(block)) continue;
      holders.push({ pageId: page.id, pageIndex, pageName: page.name, blockId: block.id });
    }
  });
  const [kept, ...repeated] = holders;
  if (kept === undefined) return [];
  return repeated.map((holder) => ({
    kind: 'person-name-repeated',
    ...holder,
    firstPage: { pageIndex: kept.pageIndex, pageName: kept.pageName },
  }));
}

/**
 * The SEVEN refusals of the feedback page (retour-experience.md §7.3, issue
 * #176): its place, its unicity, the one Bouton that sends it, the Bouton that
 * must not leave it, its phrase of thanks - and the options of a Sélection,
 * which is a block of any page and is judged here for lack of a better home.
 *
 * Judged on the structure alone, like the timeline of the noyau: no lookup, no
 * base. One problem per offence, each naming its page and, when a block is at
 * fault, the block - so every line of the save box links somewhere (§9.1). A
 * Bouton is « Envoyer le retour » when its action is `submit`.
 *
 * `feedback-not-last` is reported on EVERY feedback page that is not the last of
 * the timeline - two feedback pages therefore earn `feedback-repeated` on the
 * second and, on the first, the `feedback-not-last` they cannot help earning:
 * both are true, and the operator fixes the repetition first.
 *
 * An ABSENT `thanks` is not empty: it reads as the template's phrase
 * (`feedbackThanksOf`), and only a phrase that is PRESENT and holds nothing is
 * refused. The two Sélection options that differ by their case are two
 * options: the label written is the value kept, and nothing folds it.
 */
function feedbackProblems(content: ExperienceContent): ExperienceProblem[] {
  const problems: ExperienceProblem[] = [];
  const [firstFeedback] = content.pages.filter((page) => page.kind === 'feedback');
  const firstIndex = content.pages.findIndex((page) => page.kind === 'feedback');

  content.pages.forEach((page, pageIndex) => {
    const where = { pageId: page.id, pageIndex, pageName: page.name };
    const buttons = pageBlocks(page).filter((block) => block.type === 'button');

    if (page.kind !== 'feedback') {
      for (const button of buttons) {
        if (button.type !== 'button' || button.action !== 'submit') continue;
        problems.push({ kind: 'submit-outside-feedback', ...where, blockId: button.id });
      }
      return;
    }

    if (firstFeedback !== undefined && pageIndex !== firstIndex) {
      problems.push({
        kind: 'feedback-repeated',
        ...where,
        blockId: null,
        firstPage: { pageIndex: firstIndex, pageName: firstFeedback.name },
      });
    }
    if (pageIndex !== content.pages.length - 1) {
      problems.push({ kind: 'feedback-not-last', ...where, blockId: null });
    }

    const submits = buttons.filter((block) => block.type === 'button' && block.action === 'submit');
    if (submits.length !== 1) {
      problems.push({
        kind: 'feedback-submit-missing',
        ...where,
        blockId: null,
        submitCount: submits.length,
      });
    }

    // « Elle ne se quitte pas, elle s'envoie » (RH1): a Bouton that changes
    // page and an automatic duration are both a way out of the page. A Bouton
    // that plays the next temps stays allowed - the questions may appear by
    // temps - as it does on a page of choice of the noyau.
    for (const button of buttons) {
      if (button.type !== 'button' || button.action !== 'next-page') continue;
      problems.push({
        kind: 'feedback-page-exit',
        ...where,
        blockId: button.id,
        exit: 'next-page-button',
      });
    }
    if (page.autoDurationSeconds !== null) {
      problems.push({ kind: 'feedback-page-exit', ...where, blockId: null, exit: 'auto-duration' });
    }

    if (page.thanks !== undefined && page.thanks.trim() === '') {
      problems.push({ kind: 'feedback-thanks-empty', ...where, blockId: null });
    }
  });

  content.pages.forEach((page, pageIndex) => {
    const where = { pageId: page.id, pageIndex, pageName: page.name };
    for (const block of pageBlocks(page)) {
      if (block.type !== 'selection') continue;
      const labels = block.options.map((option) => option.trim());
      const at = { ...where, blockId: block.id };
      if (labels.length < EXPERIENCE_SELECTION_OPTIONS_MIN) {
        problems.push({ kind: 'selection-options', ...at, reason: 'too-few' });
      }
      if (labels.length > EXPERIENCE_SELECTION_OPTIONS_MAX) {
        problems.push({ kind: 'selection-options', ...at, reason: 'too-many' });
      }
      if (labels.some((label) => label === '')) {
        problems.push({ kind: 'selection-options', ...at, reason: 'empty-option' });
      }
      const written = labels.filter((label) => label !== '');
      if (new Set(written).size !== written.length) {
        problems.push({ kind: 'selection-options', ...at, reason: 'duplicate-option' });
      }
    }
  });

  return problems;
}

/**
 * The SIX refusals of a page played in the conversation (prolongation.md §3.4,
 * issue #186), judged on the structure alone like the feedback page's.
 *
 * One problem per offence, naming the page and, for an Input or a « temps
 * suivant » Bouton, the block. A page is judged when it is TICKED - its
 * `conversation` is not null - whatever its kind and its place, so a ticked page
 * out of the run earns `conversation-page-out-of-run` AND the rules of what a
 * page of the thread may carry: both are true, and the operator may well
 * untick instead of fixing the content.
 *
 * A ticked page that is not a content page (the feedback page, or the protocol
 * page) earns `conversation-page-feedback` and nothing else: the content rules
 * are about what the page carries, and it never plays in the thread.
 *
 * `conversation-page-no-exit` is waived for the LAST page of the timeline: a page
 * with no way out ends the session on arrival (§9).
 */
function conversationProblems(content: ExperienceContent): ExperienceProblem[] {
  const problems: ExperienceProblem[] = [];
  const run = new Set(conversationRunOf(content.pages));

  content.pages.forEach((page, pageIndex) => {
    if (page.conversation === null) return;
    const where = { pageId: page.id, pageIndex, pageName: page.name };

    if (page.kind !== 'content') {
      problems.push({ kind: 'conversation-page-feedback', ...where, blockId: null });
      return;
    }

    if (!run.has(page.id)) {
      problems.push({ kind: 'conversation-page-out-of-run', ...where, blockId: null });
    }
    for (const block of pageBlocks(page)) {
      if (block.type === 'input') {
        problems.push({ kind: 'conversation-page-input', ...where, blockId: block.id });
      }
      if (block.type === 'button' && block.action === 'next-temps') {
        problems.push({ kind: 'conversation-page-next-temps', ...where, blockId: block.id });
      }
    }
    if (page.autoDurationSeconds !== null) {
      problems.push({ kind: 'conversation-page-auto-duration', ...where, blockId: null });
    }
    if (!pageHasConversationExit(page) && pageIndex !== content.pages.length - 1) {
      problems.push({ kind: 'conversation-page-no-exit', ...where, blockId: null });
    }
  });

  return problems;
}

/**
 * The four refusals of §9.1, the reach of §8.6, the timeline of the noyau
 * (noyau.md §3.4), the two refusals of the revue du 2026-09-25 (§9.2 of the
 * refonte), the seven of the feedback page (retour-experience.md §7.3) and the
 * six of a page played in the conversation (prolongation.md §3.4), in
 * the order the operator reads them in the box.
 *
 * `unresolved-variable` is reported once per OCCURRENCE, because each one is a
 * place the editor links to; `duplicate-variable-name` once per offending block
 * - the second and every later one holding that name - for the same reason.
 * `variable-out-of-scope` is once per occurrence too, and it is judged AFTER
 * resolution and only on a token that resolves: an objective with no protocol
 * page, or a field nobody declares, is one defect and gets one line - the
 * unresolved one, which is the one the operator can act on first. A display
 * condition is judged exactly like a token, and its line says so.
 */
export function experienceProblems(
  content: ExperienceContent,
  context: ExperienceContext,
): ExperienceProblem[] {
  const problems: ExperienceProblem[] = [];

  // « un protocole choisi sans persona » (§9.1, §10.2). The other way round is
  // legal: an experience without a protocol is a suite of pages, and its
  // overlay simply has nobody to prolong.
  const page = protocolPageOf(content);
  if (page?.protocol != null && content.persona === null) {
    problems.push({ kind: 'protocol-without-persona' });
  }

  problems.push(...variableProblems(content, context));

  for (const citation of assetCitations(content)) {
    if (context.hasAsset(citation.assetId)) continue;
    problems.push({
      kind: 'missing-asset',
      pageId: citation.pageId,
      pageIndex: citation.pageIndex,
      pageName: citation.pageName,
      blockId: citation.blockId,
      assetId: citation.assetId,
    });
  }

  const seenNames = new Set<string>();
  for (const named of namedVariablesOf(content)) {
    if (!seenNames.has(named.name)) {
      seenNames.add(named.name);
      continue;
    }
    problems.push({
      kind: 'duplicate-variable-name',
      pageId: named.pageId,
      pageIndex: named.pageIndex,
      pageName: named.pageName,
      blockId: named.blockId,
      name: named.name,
    });
  }

  problems.push(...personNameProblems(content));
  problems.push(...noyauTimelineProblems(content));
  problems.push(...exitConditionProblems(content));
  problems.push(...feedbackProblems(content));
  problems.push(...conversationProblems(content));

  return problems;
}

/* ------------------------------------------------------------- the blockers */

/**
 * What « Passer en production » refuses (§4, strict resolution; §9.1): every
 * `latest` reference whose object has no production version, every reference
 * to a retired object whatever its pin, every id the base does not hold - and
 * the EMPTY protocol page, which is the one blocker that is not about a
 * reference.
 *
 * A reference pinned to a precise version is NOT checked for a production
 * pointer: it names that very version, which the cascade of « Passer en
 * production » carries (vitrine.md §4.2). It is still refused when its object
 * has been retired - a retired object resolves to nothing, pinned or not (§3.2).
 *
 * The `latest` check is the one this gesture still owes until the cascade
 * carries the referenced objects along (lot D): until then « Passer en
 * production » moves one pointer and nothing else, so a `latest` reference to
 * an object the production has never seen would play nothing there.
 */
export function experienceProductionBlockers(
  content: ExperienceContent,
  lookup: ExperienceReferenceLookup,
): ExperienceProductionBlocker[] {
  const blockers: ExperienceProductionBlocker[] = [];

  for (const { kind, ref } of experienceReferences(content)) {
    const state = lookup(kind, ref.id);
    if (state === undefined) {
      blockers.push({ kind: 'reference-unknown', reference: kind, id: ref.id, name: '' });
      continue;
    }
    if (state.retiredAt !== null) {
      blockers.push({
        kind: 'reference-retired',
        reference: kind,
        id: ref.id,
        name: state.name,
        retiredAt: state.retiredAt,
      });
      continue;
    }
    if (ref.pin === 'latest' && !state.hasProduction) {
      blockers.push({
        kind: 'reference-without-production',
        reference: kind,
        id: ref.id,
        name: state.name,
      });
    }
  }

  // « la page protocole n'est pas vide » (§9.1). Permitted in development - a
  // suite of pages is enough to try a theme and its texts - and refused here,
  // because a recruited person must not be sent to a conversation that is not.
  const page = protocolPageOf(content);
  if (page !== null && page.protocol === null) {
    blockers.push({
      kind: 'empty-protocol-page',
      pageId: page.pageId,
      pageIndex: page.pageIndex,
      pageName: page.pageName,
    });
  }

  // The Analyste section of the protocol is not complete (noyau.md §3.7, #154;
  // analyste.md §3.6, #196): a level no objective carries, a reading left empty.
  // A warning on a plain save, a refusal here.
  blockers.push(...analystSectionMissing(content, lookup));

  // A direction of the protocol cites an objective gone (§8.6, #158): the same.
  blockers.push(...protocolVariableOrphans(content, lookup));

  return blockers;
}

/**
 * `protocol-variable-orphan` (§8.6 of the refonte, noyau.md §4.9, V5, V6, N53;
 * issue #158), one per step, or none.
 *
 * Any experience that plays a protocol, with or without a Noyau block: an
 * unresolved variable is never rendered empty in silence (§8.6), and a
 * direction of the protocol is served to the interviewer of every session. The
 * fact is read on the version the pin the experience cites the protocol at
 * resolves to when the gesture is made, the régime of `analystSectionMissing`,
 * and it is nothing at the start of a session (N54).
 *
 * An empty protocol page cites nothing; an unknown or retired protocol, or a
 * pin with no version, is left to the reference lines, which name the fault to
 * fix first. A lookup that does not know the directions claims nothing
 * (`ExperienceReferenceState.orphanDirections`).
 */
function protocolVariableOrphans(
  content: ExperienceContent,
  lookup: ExperienceReferenceLookup,
): Extract<ExperienceWarning, { kind: 'protocol-variable-orphan' }>[] {
  const protocol = protocolPageOf(content)?.protocol ?? null;
  if (protocol === null) return [];
  const state = lookup('protocol', protocol.id);
  if (state === undefined || state.retiredAt !== null) return [];
  const atPin = state.orphanDirections?.[pinKey(protocol.pin)];
  if (atPin === undefined) return [];
  return atPin.steps.map((step) => ({
    kind: 'protocol-variable-orphan',
    reference: 'protocol',
    id: protocol.id,
    name: state.name,
    pin: protocol.pin,
    versionNumber: atPin.versionNumber,
    stepId: step.stepId,
    stepIndex: step.stepIndex,
    stepName: step.stepName,
  }));
}

/**
 * `analyst-levels-missing` and `analyst-reading-missing` (noyau.md §3.7, N35,
 * N36; issue #154; analyste.md §3.6, G21, K17; issue #196), in that order, none
 * when the section is complete.
 *
 * An experience that plays the noyau sequence - a Noyau block, and a protocol
 * on its protocol page - requires its protocol, AT THE PIN IT CITES IT AT, to
 * carry a COMPLETE Analyste section: each of the three levels carries at least
 * one objective, and both readings are non-empty. There is nothing by default
 * (P5), so a session of it would reach a determination the first pass could not
 * read. One line names every level without an objective; one line per empty
 * reading names its pass. The fact is read on the version that pin resolves to
 * when the gesture is made, the régime of the references (§3.5, P6): `latest`
 * may move after.
 *
 * Nothing is asked of a protocol page that is EMPTY - the sequence is always
 * skipped there, `noyau-sequence-skipped` says so - nor of an experience with
 * no Noyau block. An unknown or retired protocol, or a pin with no version, is
 * left to the reference lines: they name the fault to fix first, and there is
 * no version to name here. A lookup that does not know the sections claims
 * nothing (`ExperienceReferenceState.analystSections`).
 *
 * A version whose steps do not read (`levels` is `null`) names no level - they are
 * on the steps - and its readings are judged all the same: they are stored apart,
 * and a version whose content is not JSON at all reads as two empty ones.
 */
function analystSectionMissing(
  content: ExperienceContent,
  lookup: ExperienceReferenceLookup,
): Extract<ExperienceWarning, { kind: 'analyst-levels-missing' | 'analyst-reading-missing' }>[] {
  if (noyauBlocksOf(content).length === 0) return [];
  const protocol = protocolPageOf(content)?.protocol ?? null;
  if (protocol === null) return [];
  const state = lookup('protocol', protocol.id);
  if (state === undefined || state.retiredAt !== null) return [];
  const atPin = state.analystSections?.[pinKey(protocol.pin)];
  if (atPin === undefined) return [];
  const gaps = analystSectionGapsOf(atPin.levels ?? [], atPin);
  const levelsMissing = atPin.levels === null ? [] : gaps.levelsMissing;
  const place = {
    reference: 'protocol' as const,
    id: protocol.id,
    name: state.name,
    pin: protocol.pin,
    versionNumber: atPin.versionNumber,
  };
  return [
    ...(levelsMissing.length === 0
      ? []
      : [{ kind: 'analyst-levels-missing' as const, ...place, levels: [...levelsMissing] }]),
    ...gaps.readingsMissing.map((pass) => ({
      kind: 'analyst-reading-missing' as const,
      ...place,
      pass,
    })),
  ];
}

/**
 * The AVERTISSEMENTS of §9.1 - never a refusal.
 *
 * There is no longer one for « le persona n'a jamais été passé en production »
 * (vitrine.md §3.2): a `latest` reference plays « latest dev » on the atelier,
 * so it always resolves there, and what the production lacks is said by the
 * refusals of the production gesture (`reference-without-production`) and, once
 * the cascade carries the referenced objects along, by nothing at all.
 *
 * ## The two warnings of the noyau (noyau.md §3.4, §3.5; issue #151)
 *
 * Two facts are warned about and are the noyau's own:
 *
 * - the texts have NO version at all (§6.4): an unknown protocol would be a
 *   blocker and nothing more, but here the absence is the ordinary state of a
 *   base the specialist has not delivered to yet, and it stops every start of
 *   the experience, development included - so the box says it on a plain save;
 * - the protocol page is EMPTY while a Noyau block is posed: the experience
 *   plays and its sequence is always skipped, since nothing will have been
 *   gathered (§3.4) - « la page protocole est vide, la séquence noyau sera
 *   toujours sautée ».
 *
 * And, since issue #154, the protocol that has no reading for the analyst
 * (§3.7): « aucune session de cette expérience ne démarrera tant qu'il n'en aura
 * pas » - the version saves, and the same fact refuses « Passer en production ».
 * Since issue #196 the fact is the whole Analyste section (analyste.md §3.6): a
 * level that no objective carries (`analyst-levels-missing`), and either of the
 * two readings left empty (`analyst-reading-missing`, one line per pass).
 * Since issue #158, a direction of the protocol that cites an objective gone
 * (§8.6 of the refonte), per step, under the same régime.
 */
export function experienceProductionWarnings(
  content: ExperienceContent,
  lookup: ExperienceReferenceLookup,
): ExperienceWarning[] {
  const warnings: ExperienceWarning[] = [];
  for (const { kind, ref } of experienceReferences(content)) {
    const state = lookup(kind, ref.id);
    if (kind === 'noyau-texts' && state === undefined) {
      warnings.push({ kind: 'noyau-texts-missing' });
    }
  }

  const page = protocolPageOf(content);
  if (noyauBlocksOf(content).length > 0 && page !== null && page.protocol === null) {
    warnings.push({
      kind: 'noyau-sequence-skipped',
      pageId: page.pageId,
      pageIndex: page.pageIndex,
      pageName: page.pageName,
    });
  }

  // A page of the conversation with no context: the interviewer brings it with
  // no indication (prolongation.md §3.4). A warning, never a refusal.
  content.pages.forEach((candidate, pageIndex) => {
    if (candidate.kind !== 'content' || candidate.conversation === null) return;
    if (candidate.conversation.context.trim() !== '') return;
    warnings.push({
      kind: 'conversation-context-empty',
      pageId: candidate.id,
      pageIndex,
      pageName: candidate.name,
    });
  });

  warnings.push(...analystSectionMissing(content, lookup));
  warnings.push(...protocolVariableOrphans(content, lookup));
  return warnings;
}
